import { FloatingTree } from '@floating-ui/react'
import { getAssetUrl } from '@lighthouse/assets'
import { hasBlockInteraction, useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { TextBlock } from '@lighthouse/block/src/blocks'
import type { FloatBlockAbstract, TextBlockAbstract, VariableADTvalue } from '@lighthouse/core'
import { EVENT_TYPE, VariableType } from '@lighthouse/core'
import type { RichTextEditorProps } from '@lighthouse/shared'
import {
    checkActionEvent,
    fieldConvertValue,
    flexMinSizeStyle,
    getCursorStyle,
    getWithScopeIdAndRecordIdAndColumnId,
    MultipleFloatBoxProvider,
    TEXT_FONT_NORMAL,
    useActionRunningLoadings,
    useApplicationContext,
    useAtomData,
    useUnifiedTap
} from '@lighthouse/shared'
import equal from 'fast-deep-equal'
import { reduce } from 'rambda'
import React, { memo, useCallback, useEffect, useMemo } from 'react'

import { applicationSettingAtom } from '@/atoms/application/state'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useFloatBlock } from '@/hooks/useFloatBlock'
import { usePageDataQueryVariableLabel } from '@/hooks/usePageDataQueryVariableLabel'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

interface TextBlockControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: TextBlockAbstract
}

const TextBlockController: React.FC<TextBlockControllerProps> = ({ blockData, children, ...rest }) => {
    const {
        config: { eventList, floatPointerList, cursor },
        id
    } = blockData

    const { appId = '', personOptions, roleOptions, departmentOptions } = useApplicationContext()

    const { record } = useCustomViewBlockContext()
    const getPageDataQueryVariableLabel = usePageDataQueryVariableLabel()
    const parseVariable = useParseVariableWithSubscription(blockData.config.content.value)
    const parseVariable2String = useCallback(
        (value: VariableADTvalue): string => {
            const field = parseVariable(value, {
                viewRecord: record
            })
            if (!field) {
                if (value.type === VariableType.PAGE_DATA_QUERY) {
                    return getPageDataQueryVariableLabel(value)
                }
                return ''
            }
            return fieldConvertValue(field, { personOptions, roleOptions, departmentOptions })
        },
        [departmentOptions, getPageDataQueryVariableLabel, parseVariable, personOptions, record, roleOptions]
    )

    const config: RichTextEditorProps['config'] = useMemo(
        () => ({
            mode: 'text-style',
            variable: {
                renderLabel: v => parseVariable2String(v.attrs.value)
            }
        }),
        [parseVariable2String]
    )

    const textPresets = useAtomData(
        applicationSettingAtom,
        useCallback(s => s.theme.textPresetList, [])
    )

    const mergedBlockData = useMemo(() => {
        if (blockData.config.breakPoint.font.advance) {
            const advanceOption = textPresets.find(item => item.id === blockData.config.breakPoint.font.advance)
            if (advanceOption) {
                const { fontFamily, fontStyle, fontWeight, fontSize, letterSpacing, lineClamp, lineHeight } = advanceOption
                return {
                    ...blockData,
                    config: {
                        ...blockData.config,
                        breakPoint: {
                            ...blockData.config.breakPoint,
                            font: {
                                ...blockData.config.breakPoint.font,
                                fontFamily,
                                fontStyle,
                                fontWeight,
                                fontSize,
                                letterSpacing,
                                lineClamp,
                                lineHeight
                            }
                        }
                    }
                }
            }
        }

        return blockData
    }, [blockData, textPresets])

    useEffect(() => {
        const ff = mergedBlockData.config.breakPoint.font.fontFamily
        if (!ff || ff === TEXT_FONT_NORMAL) {
            return
        }
        document.fonts.ready
            .then(async ffs => {
                const fontFaceList = [...ffs.values()]
                const isLoaded = fontFaceList.some(item => item.family === ff)
                if (!isLoaded) {
                    const fontUrl = `url(${getAssetUrl('font', `${ff}.woff2`)})`
                    const newFont = new FontFace(ff, fontUrl)
                    newFont._snapdomSrc = `${getAssetUrl('font', `${ff}.woff2`)}`
                    await newFont.load()
                    ffs.add(newFont)
                }
            })

            .catch(console.warn)
    }, [mergedBlockData.config.breakPoint.font.fontFamily])

    const { scope } = useContainerBlockContext()

    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, scope, record?.id])

    const floatBlockList = useMemo(() => {
        return reduce<string, FloatBlockAbstract[]>(
            (pre, id) => {
                const block = blockData.children.find(v => v.id === id)
                return block ? [...pre, block as FloatBlockAbstract] : pre
            },
            [],
            floatPointerList ?? []
        )
    }, [blockData.children, floatPointerList])

    const provideValue = useFloatBlock(floatBlockList, scope)

    const clickEvent = useMemo(() => eventList?.find(ev => ev.type === EVENT_TYPE.CLICK), [eventList])
    const hasInteraction = useMemo(() => hasBlockInteraction({ hasAction: !!clickEvent, floatBlockList }), [clickEvent, floatBlockList])

    const { handleActionListTrigger } = useActionTrigger(id)
    const { handleActionTriggerWithLoading } = useActionRunningLoadings()

    const handleClickAction = useCallback(
        (ev: React.MouseEvent | React.TouchEvent) => {
            if (!clickEvent) {
                return
            }

            if (!checkActionEvent(ev)) {
                return
            }
            return handleActionTriggerWithLoading({
                id: uniqueId,
                actorEvent: clickEvent,
                trigger: (id, actorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record })
            })
        },
        [clickEvent, handleActionListTrigger, handleActionTriggerWithLoading, record, uniqueId]
    )

    const unifiedTap = useUnifiedTap(handleClickAction)

    const cursorValue = useMemo(() => {
        const isModify = cursor?.enable && cursor?.value === 'auto'
        if (isModify) {
            return hasInteraction ? 'pointer' : 'auto'
        }

        return getCursorStyle(appId, cursor).cursor
    }, [appId, cursor, hasInteraction])

    return (
        <FloatingTree>
            <MultipleFloatBoxProvider {...provideValue}>
                <TextBlock
                    blockData={mergedBlockData}
                    config={config}
                    resolveVariable={parseVariable2String}
                    data-stop-action-propagation={hasInteraction ? true : undefined}
                    {...unifiedTap}
                    {...rest}
                    style={{ ...flexMinSizeStyle, cursor: cursorValue, ...rest.style }}
                />
                {children}
            </MultipleFloatBoxProvider>
        </FloatingTree>
    )
}

export default memo(TextBlockController, equal)
